import { forwardRef } from "react";

import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import {
  ArrowRightIcon,
  LogoutIcon,
  ProfileIcon,
  ResetPasswordIcon,
  UserIcon
} from "@/assets/icons";
import Tooltip from "@/components/ui/tooltip";
import { MESSAGE } from "@/constants/message";
import { useAuth } from "@/hooks/auth/useAuth";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import { useAppFeatures } from "@/hooks/useAppFeatures";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import type {
  PopoverUserMenuProps,
  SidebarFooterPopoverType,
  UserMenuItemProps
} from "../types";
import FooterItemWithPopover from "./FooterItemWithPopover";

const ICON_CLASSNAME = "w-5 h-5 fill-dim-gray group-hover:fill-teal-500";
export const TOOLTIP_TRIGGER_LENGTH_MY_ACCOUNT_FULL_NAME = 18;
export const TOOLTIP_TRIGGER_LENGTH_MY_ACCOUNT_EMAIL = 35;

type UserMenuWrapperProps = {
  setActivePopover: (popoverType: SidebarFooterPopoverType) => void;
  setShowMyProfilePanel: (value: boolean) => void;
  setShowChangePasswordPanel: (value: boolean) => void;
  activePopover?: SidebarFooterPopoverType;
  isPopoverTriggerOrigin: (e: Event) => boolean;
  sidebarMenu?: boolean;
};

export const UserMenuWrapper = forwardRef<
  HTMLButtonElement,
  UserMenuWrapperProps
>(
  (
    {
      setActivePopover,
      setShowMyProfilePanel,
      setShowChangePasswordPanel,
      activePopover,
      isPopoverTriggerOrigin,
      sidebarMenu
    },
    ref
  ) => {
    return (
      <FooterItemWithPopover
        togglePopover={(open) => {
          setActivePopover(open ? "user" : "");
        }}
        isPopoverTriggerOrigin={isPopoverTriggerOrigin}
        classNamePopoverContent={twMerge(sidebarMenu && "ml-6")}
        popoverTrigger={
          <Button
            data-testid="user-popover-trigger"
            ref={ref}
            className={twMerge(
              "flex items-center justify-center p-0 rounded-[32px] bg-whisper hover:bg-teal-50",
              activePopover === "user" && "bg-teal-50",
              sidebarMenu && "w-[231px] justify-start bg-transparent"
            )}
          >
            <Tooltip
              disable={sidebarMenu}
              content="My Account"
              position="right"
              key="My Account"
            >
              <div className="flex items-center justify-center">
                <UserIcon
                  className={twMerge(
                    "fill-dim-gray hover:fill-teal-500 w-9 h-9 p-2",
                    activePopover === "user" && "fill-teal-500",
                    sidebarMenu && "rounded-[32px] bg-whisper hover:bg-teal-50"
                  )}
                />
                <span
                  className={twMerge(
                    "whitespace-pre text-sm font-medium",
                    sidebarMenu
                      ? "pl-1.5"
                      : "opacity-0 translate-x-28 overflow-hidden hidden",
                    activePopover === "user"
                      ? "text-black font-bold"
                      : "text-dim-gray"
                  )}
                >
                  My Account
                </span>
              </div>
            </Tooltip>
          </Button>
        }
        popoverContent={
          <UserMenu
            setActivePopover={setActivePopover}
            setShowMyProfilePanel={setShowMyProfilePanel}
            setShowChangePasswordPanel={setShowChangePasswordPanel}
          />
        }
        isOpen={activePopover === "user"}
      />
    );
  }
);

const UserMenuItem = ({
  label,
  icon,
  hasArrow = false,
  onClick
}: UserMenuItemProps) => (
  <Button
    className="w-full justify-start gap-2 hover:bg-teal-50 p-2 rounded-xl group"
    onClick={onClick}
  >
    <div className="flex flex-1 gap-2">
      {icon}
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-txtHeading group-hover:text-teal-500">
          {label}
        </span>
      </div>
    </div>
    {hasArrow && (
      <ArrowRightIcon className="fill-dim-gray w-4 h-4 group-hover:fill-teal-500" />
    )}
  </Button>
);

const UserMenu = ({
  setActivePopover,
  setShowMyProfilePanel,
  setShowChangePasswordPanel
}: PopoverUserMenuProps) => {
  const { isChangePasswordEnabled } = useAppFeatures();
  const { logout } = useAuth();
  const { data: userProfileData } = useGetUserProfileAPI();

  const { showConfirmation } = useConfirmationModal();

  const isTooltipFullNameEnabled =
    (userProfileData?.full_name?.length ?? 0) >
    TOOLTIP_TRIGGER_LENGTH_MY_ACCOUNT_FULL_NAME;

  const isTooltipEmailAddressEnabled =
    (userProfileData?.email_address?.length ?? 0) >
    TOOLTIP_TRIGGER_LENGTH_MY_ACCOUNT_EMAIL;

  /* Click Cancel and Logout buttons from the modal will just close the confirmation modal for
  now until actual endpoint is implemented by ORB-505 */
  const handleLogout = () => {
    setActivePopover("");
    showConfirmation({
      title: "Log Out?",
      message: MESSAGE.CONFIRM_LOG_OUT,
      cancelLabel: "",
      confirmLabel: "Logout",
      onConfirm: logout
    });
  };

  return (
    <div className="flex flex-col gap-3 p-3" data-testid="user-menu">
      <div className="flex gap-2">
        <ProfileIcon className="w-[36px] h-[36px] fill-dim-gray" />
        <div className="flex flex-col max-w-[170px]">
          <Tooltip
            content={userProfileData?.full_name}
            position="top-center"
            alignOffset={-6}
            sideOffset={2}
            className=""
            disable={!isTooltipFullNameEnabled}
          >
            <span className="text-sm font-semibold text-txtHeading truncate text-ellipsis">
              {userProfileData?.full_name}
            </span>
          </Tooltip>
          <Tooltip
            content={userProfileData?.email_address}
            position="top-center"
            alignOffset={-6}
            sideOffset={2}
            className=""
            disable={!isTooltipEmailAddressEnabled}
          >
            <span className="text-[10px] text-txtBody">
              {userProfileData?.email_address}
            </span>
          </Tooltip>
        </div>
      </div>
      <div className="flex flex-col">
        <UserMenuItem
          label="My profile"
          icon={<ProfileIcon className={ICON_CLASSNAME} />}
          onClick={() => {
            setShowMyProfilePanel(true);
            setActivePopover("");
          }}
        />
        {isChangePasswordEnabled && (
          <UserMenuItem
            label="Change Password"
            icon={<ResetPasswordIcon className={ICON_CLASSNAME} />}
            onClick={() => {
              setShowChangePasswordPanel(true);
              setActivePopover("");
            }}
          />
        )}
        <UserMenuItem
          label="Log out"
          icon={<LogoutIcon className={ICON_CLASSNAME} />}
          onClick={handleLogout}
        />
      </div>
    </div>
  );
};

export default UserMenuWrapper;
